<template>
  <h2 v-if="false === confirmationSuccess">
    {{ $t("confirmEmailSetPassword.error") }}
  </h2>
  <h2 v-if="true === confirmationSuccess">
    {{ $t("confirmEmailSetPassword.confirmed") }}
  </h2>
  <form v-if="true === confirmationSuccess && undefined !== passwordToken" class="flex flex-col gap-y-4" @submit.prevent="resetPassword">
    <h2>{{ $t("confirmEmailSetPassword.title") }}</h2>
    <div class="py-12 flex flex-col gap-y-6">
      <InputField
        v-model="passwords.password"
        required
        :error="errors?.password"
        type="password"
        :label="$t('resetPassword.passwordLabel')"
      />
      <InputField
        v-model="passwords.passwordRepeat"
        type="password"
        required
        :error="errors?.passwordRepeat"
        :label="$t('resetPassword.passwordRepeatLabel')"
      />
    </div>
    <div class="flex gap-x-4 mt-3">
      <custom-button
        type="submit"
        variant="primary"
        class="flex-1"
        :busy="loading"
      >
        {{ $t("confirmEmailSetPassword.buttonText") }}
      </custom-button>
    </div>
  </form>
  <Spinner v-if="loading && true !== confirmationSuccess" />
</template>

<script lang="ts" setup>
import { computed, reactive, ref } from 'vue';
import { useFetchAction } from '@hooks/fetch';
import { useRoute, useRouter } from 'vue-router';
import InputField from '@components/utils/inputs/Input.vue';
import { object, string, ref as yupRef } from 'yup';
import { useI18n } from 'vue-i18n';
import CustomButton from '@components/utils/CustomButton.vue';
import Spinner from '@/components/utils/Spinner.vue';
import { useValidation } from '@/hooks/form';
import { useNotify } from '@/components/utils/notifications';

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const confirmationToken = computed(() => route.params.token);
const passwordToken = ref(undefined);

const schema = object({
  password: string().required(t('pages.profile.sections.password.passwordRequired')),
  passwordRepeat: string().oneOf(
    [yupRef('password'), null],
    t('pages.profile.sections.password.passwordMismatch'),
  ),
});

const {
  data: passwords,
  errors,
  validatedFn,
} = useValidation(
  schema,
  reactive({
    password: '',
    passwordRepeat: '',
  }),
);

const [exec, { loading }] = useFetchAction();

const notify = useNotify();

const confirmationSuccess = ref<boolean|undefined>(undefined);

async function confirmEmail() {
  try {
    const { passwordResetToken } = await exec('/users/confirm-email', {
      method: 'POST',
      body: {
        token: confirmationToken.value,
      },
    });

    confirmationSuccess.value = true;
    passwordToken.value = passwordResetToken;
  } catch (e) {
    confirmationSuccess.value = false;
  }
}

confirmEmail();

const resetPassword = validatedFn(async () => {
  try {
    await exec('/users/reset-password', {
      method: 'POST',
      body: {
        password: passwords.value.password,
        token: passwordToken.value,
      },
    });
    notify?.({
      variant: 'success',
      title: t('resetPassword.saveSuccess'),
    });

    await router.push({ name: 'login' });
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('resetPassword.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped></style>
